import type z from 'zod';
import React from 'react';
import { Building, Building2, Earth, Hash, Mail, MapPin, Phone } from 'lucide-react';

import type { ReportSchema } from '@ballerine/common';

import { ctw } from '@/common';
import {
  filterValidStrings,
  resolveMerchantAddressDisplay,
  resolveMerchantEmailDisplay,
  resolveMerchantPhoneDisplay,
} from '@/components/templates/composites/MerchantDetailsContent/utils';
import { DataRow } from '@/components/templates/primitives';

type DFMerchantDetailsContentProps = {
  merchantDetails: NonNullable<z.infer<typeof ReportSchema>['data']>['merchantDetails'];
  className?: string;
};

export const DFMerchantDetailsContent: React.FC<DFMerchantDetailsContentProps> = ({
  merchantDetails,
  className,
}) => {
  const phoneDisplay = resolveMerchantPhoneDisplay(
    merchantDetails as typeof merchantDetails & {
      phoneNumbers?: string[] | null;
    },
  );
  const emailDisplay = resolveMerchantEmailDisplay(
    merchantDetails as typeof merchantDetails & {
      emailAddresses?: string[] | null;
    },
  );
  const addressDisplay = resolveMerchantAddressDisplay(
    merchantDetails as typeof merchantDetails & {
      physicalAddresses?: string[] | null;
    },
  );
  const alternativeCompanyNames = filterValidStrings(merchantDetails?.alternativeCompanyNames);

  return (
    <div className={ctw(className)}>
      <DataRow
        label="Company Name"
        Icon={Building}
        value={merchantDetails?.companyName}
        copyable={!!merchantDetails?.companyName}
        copyValue={merchantDetails?.companyName || ''}
        disableTranslation
      />

      <DataRow
        label="Alternative Company Names"
        Icon={Building2}
        value={
          alternativeCompanyNames.length ? <span>{alternativeCompanyNames.join(', ')}</span> : null
        }
        copyable={!!alternativeCompanyNames.length}
        copyValue={alternativeCompanyNames.join(', ') || ''}
        disableTranslation
      />

      <DataRow
        label="Registration Number"
        Icon={Hash}
        value={merchantDetails?.registrationNumber}
        copyable={!!merchantDetails?.registrationNumber}
        copyValue={merchantDetails?.registrationNumber || ''}
      />

      <DataRow
        label="Phone Number"
        Icon={Phone}
        value={phoneDisplay.text}
        copyable={Boolean(phoneDisplay.text)}
        copyValue={phoneDisplay.copy}
      />

      <DataRow
        label="Email"
        Icon={Mail}
        value={emailDisplay.text}
        copyable={Boolean(emailDisplay.text)}
        copyValue={emailDisplay.copy}
        disableTranslation
      />

      <DataRow
        label="Country"
        Icon={Earth}
        value={merchantDetails?.country}
        copyable={!!merchantDetails?.country}
        copyValue={merchantDetails?.country || ''}
      />

      <DataRow
        label="Address"
        Icon={MapPin}
        value={addressDisplay.text}
        copyable={Boolean(addressDisplay.text)}
        copyValue={addressDisplay.copy}
      />
    </div>
  );
};
